import React, { useEffect, useState } from 'react';
import { getInvitationInfo } from '@/services/user-invitations';
import { Trans } from '@lingui/react/macro';
import { LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { connect, useSelector } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router';
import { IState } from '@/state/IState';
import { InviteType, InvitationWithToken, TranslatableText } from '@/types';
import { useQueryString, useAppDispatch, useCurrentCulture } from '@/utils/hooks';
import { setCurrentCulture } from '@/state/global/creators';
import { tryGetCultureInfo } from '@/utils/localization';
import SplashPage from './SplashPage';
import { getProjectHeaderImageUrl } from '@/services/projects';
import InvitationDetailsBuyerOrProfile from './InvitationDetailsBuyerOrProfile';
import InvitationDetailsProject from './InvitationDetailsProject';
import { InternalLinkButton } from '@/components/Buttons';
import { HttpStatusCode } from '@/HttpStatusCodes';
import { CustomRedirect } from '@/components/navigation';
import theme from './InvitationDetails.module.scss';

const mapStateToProps = (state: IState) => ({
    user: state.global.user!,
});

type OwnProps = ReturnType<typeof mapStateToProps> & RouteComponentProps<any> & {};

interface QueryStringParams {
    token: string;
}

const InvitationDetails: React.FC<OwnProps> = ({ location }) => {
    const [invitation, setInvitation] = useState<InvitationWithToken | null>(null);
    const [autoRedirect, setAutoRedirect] = useState(false);
    const [errorMessage, setErrorMessage] = useState<TranslatableText | null>(null);
    const { token } = useQueryString<QueryStringParams>(location);
    const user = useSelector((state: IState) => state.global.user);
    const dispatch = useAppDispatch();
    const [currentCulture] = useCurrentCulture();

    useEffect(() => {
        async function loadData() {
            try {
                if (!token) {
                    throw Error('Invalid token');
                }

                setInvitation({ ...(await getInvitationInfo(token)), token });
            } catch (e: any) {
                if (e.status === HttpStatusCode.BadRequest) {
                    setAutoRedirect(true);
                    setErrorMessage(<Trans>This invitation has already been accepted.</Trans>);
                } else {
                    setErrorMessage(<Trans>This invitation does not seem to be valid.</Trans>);
                }
            }
        }

        loadData();
    }, [token]);

    useEffect(() => {
        const language = invitation?.language;
        if (!language || user?.isLoggedIn) return;
        const culture = tryGetCultureInfo(language);
        if (culture && currentCulture?.locale !== culture.locale) {
            setCurrentCulture(dispatch, culture);
        }
    }, [invitation, user, currentCulture, dispatch]);

    if (autoRedirect && user && user.isLoggedIn) {
        return <CustomRedirect to="/" />;
    }

    if (errorMessage) {
        return (
            <SplashPage>
                <div className={theme.error}>
                    <h3>{errorMessage}</h3>

                    <InternalLinkButton color="primary" size="large" to="/">
                        <Trans>Proceed to login</Trans>
                    </InternalLinkButton>

                    <small>
                        <Trans>
                            If you think there is an issue with the invitation please contact the person who invited you.
                        </Trans>
                    </small>
                </div>
            </SplashPage>
        );
    }

    if (!invitation) {
        return <LoadingIndicator />;
    }

    const renderInviteDetails = () => {
        switch (invitation.inviteType) {
            case InviteType.Buyer:
                return (
                    <InvitationDetailsBuyerOrProfile
                        greetingText={
                            <Trans>
                                Welcome and congratulations for your new home! <br />
                                {invitation.invitedTo}
                            </Trans>
                        }
                        invitation={invitation}
                    />
                );
            case InviteType.Profile:
                return (
                    <InvitationDetailsBuyerOrProfile
                        greetingText={<Trans>You are invited to collaborate on {invitation.invitedTo}.</Trans>}
                        invitation={invitation}
                    />
                );
            case InviteType.Project:
                return <InvitationDetailsProject invitation={invitation} />;
        }
    };

    return (
        <SplashPage backgroundImageUrl={invitation.projectId ? getProjectHeaderImageUrl(invitation.projectId) : undefined}>
            <h3>
                <Trans>Hi {invitation.inviteeFirstName || ''}!</Trans>
            </h3>

            {renderInviteDetails()}
            {/* <form onSubmit={handleFormSubmit}>{renderActions()}</form> */}
        </SplashPage>
    );
};

export default connect(mapStateToProps)(withRouter(InvitationDetails));
